import React, {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useState
} from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import toast from "react-hot-toast";
import {
  useBookLoad,
  useCarriers,
  useDeleteSavedLoad,
  useSavedLoads,
  useSaveLoad,
  useSearchLoads,
  useStartSSESearch,
  useUserProfile,
} from "../queries/loads";
import { useAuth } from "../contexts/AuthContext";
import { useAnalytics } from "../utils/useAnalytics";
import { useSearchTabsController } from "../hooks/useSearchTabsController";
import { useLocalStorage } from "../hooks/useLocalStorage";
import { useViewedLoads } from "../hooks/useViewedLoads";
import { useLoadViewTracking } from "../hooks/useLoadViewTracking";
import { searchSchema, defaultSearchValues } from "../schemas/search";
import {
  buildSavePayload,
  getLoadIdentifier,
  normalizeLoad,
} from "../domain/load";
import { downloadCsv } from "../utils/csv";
import { sendCarrierPacket } from "../utils/email";
import { getCurrentLocationCityState } from "../utils/location";
import { INTERNAL_EMAILS } from "../utils/internalEmails";
import { toNumber } from "../utils/loadFormatting";
import NewLoadSearchView from "../components/NewLoadSearch/NewLoadSearchView";
import LoadDetailsModal from "../components/LoadDetailsModal";
import { useAutoRefreshTabs } from "../hooks/useAutoRefreshTabs";
import { useMultiTabSSE } from "../hooks/useMultiTabSSE";

const DAILY_SEARCH_LIMIT = 7;
const AUTO_REFRESH_ENABLED = true
const AUTO_REFRESH_INTERVAL_MS = 180000;
const AUTO_REFRESH_STAGGER_MS = 5000;

const initialUIState = ({ showSaved, showSearchForm }) => ({
  selectedLoad: { data: null, mode: "idle" },
  sidebarOpen: false,
  showSaved,
  showSearchForm,
  error: null,
});

function loadSearchReducer(state, action) {
  switch (action.type) {
    case "select-load":
      return {
        ...state,
        selectedLoad: {
          data: action.load,
          mode: action.load ? "idle" : "idle",
        },
        sidebarOpen: Boolean(action.load),
      };
    case "set-mode":
      return {
        ...state,
        selectedLoad: {
          data: action.load ?? state.selectedLoad.data,
          mode: action.mode,
        },
      };
    case "close-sidebar":
      return {
        ...state,
        sidebarOpen: false,
        selectedLoad: { data: null, mode: "idle" },
      };
    case "set-show-saved":
      return { ...state, showSaved: action.value };
    case "toggle-search-form":
      return { ...state, showSearchForm: !state.showSearchForm };
    case "set-error":
      return { ...state, error: action.error };
    default:
      return state;
  }
}

const NewLoadSearch = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { trackLoadSave, trackLoadBook } = useAnalytics(user);


  const [savedPref, setSavedPref] = useLocalStorage(
    "newLoadSearchShowSaved",
    false,
  );
  const [formPref, setFormPref] = useLocalStorage(
    "newLoadSearchShowForm",
    true,
  );
  const [selectedCarrier, setSelectedCarrier] = useLocalStorage(
    "selectedCarrier",
    null,
  );
  const [trialData, setTrialData] = useLocalStorage("trialSearchData", {
    date: new Date().toDateString(),
    count: 0,
  });
  const [showPartners, setShowPartners] = useLocalStorage(
    "showPartnersColumn",
    false,
  );

  // Viewed loads management
  const {
    viewedLoadIds,
    markLoadAsViewed,
    markMultipleLoadsAsViewed,
  } = useViewedLoads();

  // Selection mode state for bulk marking as viewed
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [selectedLoadIds, setSelectedLoadIds] = useState(new Set());

  const [state, dispatch] = useReducer(
    loadSearchReducer,
    initialUIState({ showSaved: savedPref, showSearchForm: formPref }),
  );

  // Track load detail views with time-based threshold (5 seconds)
  // This sends events to backend and PostHog when user views load details
  useLoadViewTracking({
    load: state.selectedLoad.data,
    isOpen: state.sidebarOpen,
    userEmail: user?.email,
  });

  useEffect(() => {
    setSavedPref(state.showSaved);
  }, [state.showSaved, setSavedPref]);

  useEffect(() => {
    setFormPref(state.showSearchForm);
  }, [state.showSearchForm, setFormPref]);

  const today = useMemo(() => new Date().toDateString(), []);
  const isTrialUser = user?.subscription_type === 1;
  const trialCount = trialData?.date === today ? trialData.count : 0;
  const searchLimitReached = isTrialUser && trialCount >= DAILY_SEARCH_LIMIT;
  

  useEffect(() => {
    if (trialData?.date !== today) {
      setTrialData({ date: today, count: 0 });
    }
  }, [setTrialData, today, trialData?.date]);

  const tabsController = useSearchTabsController();
  const {
    tabs,
    activeTab,
    activeTabId,
    setActiveTabId,
    addTab,
    removeTab,
    setResults,
    setLoading,
    mergeResultsByTab,
    setTabLoadingById,
    setSortForActiveTab,
    setActiveTabSearchId,
  } = tabsController;

  const form = useForm({
    resolver: zodResolver(searchSchema),
    defaultValues: {
      ...defaultSearchValues,
      ...(activeTab?.formData || {}),
      enableBackhaul:
        activeTab?.formData?.enableBackhaul ??
        activeTab?.enableBackhaul ??
        defaultSearchValues.enableBackhaul,
    },
  });

  useEffect(() => {
    if (activeTab?.formData) {
      form.reset({
        ...defaultSearchValues,
        ...activeTab.formData,
        enableBackhaul:
          activeTab.formData?.enableBackhaul ??
          activeTab.enableBackhaul ??
          defaultSearchValues.enableBackhaul,
      });
    }
  }, [
    activeTab?.id,
    activeTab?.formData,
    activeTab?.enableBackhaul,
    form,
  ]);

  useEffect(() => {
    const subscription = form.watch((values) => {
      tabsController.setFormValues(values);
    });
    return () => subscription.unsubscribe();
  }, [form, tabsController]);

  const { data: profileData, isLoading: profileLoading } = useUserProfile();
  const isDispatcher = profileData?.account_type === "dispatcher";
  const { data: carriersData = [] } = useCarriers(isDispatcher);
  const carriers = useMemo(() => carriersData || [], [carriersData]);

  useEffect(() => {
    if (!isDispatcher || !selectedCarrier) {
      return;
    }
    const exists = carriers.some(
      (carrier) => carrier.email === selectedCarrier.email,
    );
    if (!exists) {
      setSelectedCarrier(null);
    }
  }, [isDispatcher, carriers, selectedCarrier, setSelectedCarrier]);

  const handleCarrierChange = (event) => {
    const carrier =
      carriers.find((carrierItem) => carrierItem.email === event.target.value) ||
      null;
    setSelectedCarrier(carrier);
    if (carrier) {
      form.setValue(
        "max_weight",
        carrier.max_weight || form.getValues("max_weight"),
      );
      form.setValue(
        "max_length",
        carrier.max_length || form.getValues("max_length"),
      );
      if (carrier.truck_type) {
        form.setValue("truck_types", [carrier.truck_type]);
      }
    }
  };

  const savedLoadsQuery = useSavedLoads(true);
  const savedLoads = useMemo(
    () => savedLoadsQuery.data || [],
    [savedLoadsQuery.data],
  );
  const { isFetching: isFetchingSavedLoads } = savedLoadsQuery;

  const searchMutation = useSearchLoads();
  const startSSEMutation = useStartSSESearch();
  const saveLoadMutation = useSaveLoad();
  const deleteSavedLoadMutation = useDeleteSavedLoad();
  const bookLoadMutation = useBookLoad();

  const lastSearchType = activeTab?.lastSearch?.searchType;
  const hasBackhaulResults = lastSearchType === "backhaul";
  const hasStandardResults = lastSearchType === "standard";

  const backhaulResults = useMemo(() => {
    if (!hasBackhaulResults || state.showSaved) {
      return [];
    }
    if (!Array.isArray(activeTab?.results)) {
      return [];
    }
    // Filter out invalid pairs that don't have both outbound and return loads
    return activeTab.results.filter((pair) => pair?.outbound && pair?.return);
  }, [activeTab?.results, hasBackhaulResults, state.showSaved]);

  const isBackhaulEnabled =
    Boolean(activeTab?.formData?.enableBackhaul) ||
    Boolean(activeTab?.enableBackhaul);

  const standardResults = useMemo(() => {
    if (state.showSaved) {
      return savedLoads;
    }
    if (!activeTab || !hasStandardResults || isBackhaulEnabled) {
      return [];
    }
    return Array.isArray(activeTab.results)
      ? activeTab.results.map((load) => normalizeLoad(load))
      : [];
  }, [state.showSaved, savedLoads, activeTab, hasStandardResults, isBackhaulEnabled]);

  const currentSort = activeTab?.sort || "score";

  const sortedResults = useMemo(() => {
    const list = [...standardResults];

    const sorterMap = {
      score: (a, b) => (toNumber(b.score) || 0) - (toNumber(a.score) || 0),
      ratePerMile: (a, b) =>
        (toNumber(b.rate_per_mile_est) || 0) -
        (toNumber(a.rate_per_mile_est) || 0),
      weight: (a, b) => (toNumber(b.weight) || 0) - (toNumber(a.weight) || 0),
      distance_high_to_low: (a, b) =>
        (toNumber(b.distance) || 0) - (toNumber(a.distance) || 0),
      distance_low_to_high: (a, b) =>
        (toNumber(a.distance) || 0) - (toNumber(b.distance) || 0),
      revenue: (a, b) =>
        (toNumber(b.payment) || 0) - (toNumber(a.payment) || 0),
      deadhead: (a, b) =>
        (toNumber(a.origin_distance) || toNumber(a.dead_head) || 0) -
        (toNumber(b.origin_distance) || toNumber(b.dead_head) || 0),
      age: (a, b) => (toNumber(a.age) || 0) - (toNumber(b.age) || 0),
    };

    const sorter = sorterMap[currentSort] || sorterMap.score;

    list.sort((a, b) => {
      // Primary sort: selected sort option
      const sortDiff = sorter(a, b);
      if (sortDiff !== 0) {
        return sortDiff;
      }

      // Secondary sort: newest loads first (for stable ordering of ties)
      const insertedDiff = (b.__insertedAt || 0) - (a.__insertedAt || 0);
      return insertedDiff;
    });

    return list;
  }, [standardResults, currentSort]);

  const showBackhaulView =
    isBackhaulEnabled && !state.showSaved && hasBackhaulResults;

  const resultsCount = showBackhaulView
    ? backhaulResults.length
    : sortedResults.length;

  const isInternalUser =
    Boolean(user?.email) && INTERNAL_EMAILS.includes(user.email);

  const searchParams = activeTab?.formData || defaultSearchValues;

  const handleAutoRefresh = useCallback(
    async (tab) => {
      if (!tab?.lastSearch?.query) {
        return;
      }

      // Skip auto-refresh for tabs that already have an active SSE search session.
      // SSE connections stream fresh data continuously, so polling via HTTP would be redundant.
      if (tab.searchId) {
        console.log("TAB SSE SEARCH ACTIVE", tab.searchId);
        console.log(
          `[AUTO-REFRESH] Skipping refresh for tab ${tab.id} - SSE search active (searchId: ${tab.searchId})`,
        );
        return;
      }

      const profileSnapshot =
        tab.lastSearch.profileSnapshot || profileData;
      if (!profileSnapshot) {
        return;
      }
      const carrierSnapshot =
        tab.lastSearch.carrierSnapshot !== undefined
          ? tab.lastSearch.carrierSnapshot
          : selectedCarrier;
      try {
        setTabLoadingById(tab.id, true);
        const result = await searchMutation.mutateAsync({
          profile: profileSnapshot,
          carrier: carrierSnapshot,
          values: tab.lastSearch.query,
        });

        mergeResultsByTab(tab.id, result.results, {
          searchType: result.type,
          query: tab.lastSearch.query,
          profileSnapshot,
          carrierSnapshot,
        });
      } catch (error) {
        console.warn("[AUTO-REFRESH] Failed to refresh tab", tab.id, error);
      } finally {
        setTabLoadingById(tab.id, false);
      }
    },
    [
      mergeResultsByTab,
      profileData,
      searchMutation,
      selectedCarrier,
      setTabLoadingById,
    ],
  );

  useAutoRefreshTabs({
    enabled: AUTO_REFRESH_ENABLED && Boolean(profileData),
    tabs,
    refreshIntervalMs: AUTO_REFRESH_INTERVAL_MS,
    staggerDelayMs: AUTO_REFRESH_STAGGER_MS,
    onRefresh: handleAutoRefresh,
  });

  // Handler for SSE loads from all sources
  // Now receives tabId as first parameter (from useMultiTabSSE)
  const handleSSELoads = useCallback(
  (tabId, loads, source, status) => {
    if (!tabId) return;

    // Find the tab by ID
    const targetTab = tabs.find((tab) => tab.id === tabId);
    if (!targetTab) {
      console.warn(`[SSE] Tab ${tabId} not found`);
      return;
    }

    // Clear SSE pending flag on first update
    const lastSearch = targetTab.lastSearch || {};
    const hasSSEStarted = lastSearch.ssePending;

    // Get current results to check for duplicates
    const currentResults = Array.isArray(targetTab.results) ? targetTab.results : [];
    const existingIds = new Set(currentResults.map((l) => getLoadIdentifier(l)));
    const newLoads = loads.filter((l) => {
      const id = getLoadIdentifier(l);
      return id && !existingIds.has(id);
    });

    if (newLoads.length > 0) {
      const sourceName = source === "tql" ? "TQL" :
        source === "chrobinson" ? "CH Robinson" :
          source === "parade" ? "Parade" :
            source === "truckstop" ? "Truckstop" : source;
      
      if (hasSSEStarted) {
        toast.success(`Search complete! Found ${newLoads.length} load${newLoads.length > 1 ? 's' : ''} from ${sourceName}`);
      }

      // Use mergeResultsByTab with the tabId
      mergeResultsByTab(tabId, newLoads, {
        searchType: "standard",
        source,
        status,
        ssePending: false, // Clear the pending flag
      });
      
      // Only set loading to false if this is the first update
      if (hasSSEStarted && targetTab.loading) {
        setTabLoadingById(tabId, false);
      }
    } else if (hasSSEStarted) {
      // If SSE started but no new loads, still clear the loading state and ssePending flag
      mergeResultsByTab(tabId, [], {
        searchType: "standard",
        source,
        status,
        ssePending: false, // Clear the pending flag even with no loads
      });
      
      if (targetTab.loading) {
        setTabLoadingById(tabId, false);
      }
    }
  },
  [tabs, mergeResultsByTab, setTabLoadingById]
);

  // Handler for SSE errors
  const handleSSEError = useCallback((tabId, error) => {
    if (error.includes("expired")) {
      toast.error("Search expired. Please start a new search.", {
        duration: 5000,
      });
    } else {
      toast.error(`Search error: ${error}`, {
        duration: 5000,
      });
    }
  }, []);

  // Use multi-tab SSE for real-time updates
  // This manages SSE connections for ALL tabs with searchId simultaneously
  useMultiTabSSE(tabs, handleSSELoads, handleSSEError);

  const onSubmit = form.handleSubmit(async (values) => {
  const storedValues = JSON.parse(JSON.stringify(values));
  if (!activeTab || !profileData) return toast.error("Profile loading...");

  setLoading(true);
  dispatch({ type: "set-error", error: null });

  // Clear previous search ID for this tab (if any)
  if (activeTab?.searchId) {
    setActiveTabSearchId(null);
  }

  try {
    const isBackhaul = Boolean(values.enableBackhaul);

    // For backhaul searches, use synchronous search (SSE not supported)
    if (isBackhaul) {
      const result = await searchMutation.mutateAsync({
        profile: profileData,
        carrier: selectedCarrier,
        values: storedValues,
      });

      setResults(result.results, {
        searchType: result.type,
        query: storedValues,
        profileSnapshot: profileData,
        carrierSnapshot: selectedCarrier,
      });
      setLoading(false);
    } else {
      // For standard searches, use SSE for real-time updates
      try {
        const sseResult = await startSSEMutation.mutateAsync({
          profile: profileData,
          carrier: selectedCarrier,
          values: storedValues,
        });

        const newSearchId = sseResult.search_id;

        // Store searchId in the tab's state (not component state)
        // This allows multiple tabs to have independent SSE connections
        setActiveTabSearchId(newSearchId);

        // Initialize with empty results AND set a flag that SSE is pending
        setResults([], {
          searchType: "standard",
          query: storedValues,
          profileSnapshot: profileData,
          carrierSnapshot: selectedCarrier,
          ssePending: true, // Add this flag
        });

        toast.success("Search started. Results will appear as they become available.");
        
        // Don't set loading to false yet - keep showing loading indicator
        // until we receive the first SSE update
      } catch (sseError) {
        // Fallback to synchronous search if SSE fails
        const result = await searchMutation.mutateAsync({
          profile: profileData,
          carrier: selectedCarrier,
          values: storedValues,
        });

        setResults(result.results, {
          searchType: result.type,
          query: storedValues,
          profileSnapshot: profileData,
          carrierSnapshot: selectedCarrier,
          ssePending: false,
        });
        setLoading(false);
      }
    }

  } catch (error) {
    const message = error?.message || "Failed to fetch loads.";
    dispatch({ type: "set-error", error: message });
    toast.error(message);
    setResults([], {
      searchType: values.enableBackhaul ? "backhaul" : "standard",
      query: storedValues,
      profileSnapshot: profileData,
      carrierSnapshot: selectedCarrier,
      ssePending: false,
    });
    setLoading(false);
  }
});


  const updateActiveResults = useCallback(
    (loadId, updater) => {
      if (!activeTab || isBackhaulEnabled) {
        return;
      }
      const currentResults = Array.isArray(activeTab.results)
        ? activeTab.results
        : [];
      if (!currentResults.length) {
        return;
      }
      const updated = currentResults.map((load) => {
        if (getLoadIdentifier(load) !== loadId) {
          return load;
        }
        return typeof updater === "function"
          ? updater(load)
          : { ...load, ...updater };
      });
      setResults(updated, activeTab.lastSearch);
    },
    [activeTab, isBackhaulEnabled, setResults],
  );

  const handleSelectLoad = useCallback(
    (load) => {
      if (!load) {
        return;
      }
      // Mark load as viewed when drawer opens
      const loadId = getLoadIdentifier(load);
      if (loadId) {
        markLoadAsViewed(loadId);
      }
      dispatch({ type: "select-load", load: normalizeLoad(load) });
    },
    [markLoadAsViewed]
  );

  const handleCloseSidebar = () => {
    dispatch({ type: "close-sidebar" });
  };

  // Toggle select mode for bulk operations
  const handleToggleSelectMode = useCallback(() => {
    setIsSelectMode((prev) => {
      if (prev) {
        // Exiting select mode - clear selections
        setSelectedLoadIds(new Set());
      }
      return !prev;
    });
  }, []);

  // Toggle individual load selection
  const handleToggleLoadSelection = useCallback((loadId) => {
    if (!loadId) return;
    setSelectedLoadIds((prev) => {
      const next = new Set(prev);
      if (next.has(String(loadId))) {
        next.delete(String(loadId));
      } else {
        next.add(String(loadId));
      }
      return next;
    });
  }, []);

  // Mark all selected loads as viewed
  const handleMarkSelectedAsViewed = useCallback(() => {
    if (selectedLoadIds.size === 0) return;
    markMultipleLoadsAsViewed([...selectedLoadIds]);
    // Exit select mode and clear selections
    setSelectedLoadIds(new Set());
    setIsSelectMode(false);
    toast.success(
      `Marked ${selectedLoadIds.size} load${selectedLoadIds.size === 1 ? "" : "s"} as viewed`
    );
  }, [selectedLoadIds, markMultipleLoadsAsViewed]);

  const handleToggleSavedLoads = () => {
    dispatch({ type: "set-show-saved", value: !state.showSaved });
  };

  const handleSortChange = (value) => {
    setSortForActiveTab(value);
  };

  const handleRefreshSavedLoads = () => {
    savedLoadsQuery.refetch();
  };

  const handleDownloadCsv = () => {
    if (showBackhaulView) {
      toast.error("CSV export is only available for standard results.");
      return;
    }
    if (!sortedResults.length) {
      toast.error("No results available to export.");
      return;
    }
    try {
      downloadCsv(
        sortedResults,
        state.showSaved ? "saved-loads.csv" : "load-search-results.csv",
      );
      toast.success("CSV download started.");
    } catch (error) {
      toast.error("Unable to export CSV.");
    }
  };

  const handleGetCurrentLocation = async () => {
    try {
      const location = await getCurrentLocationCityState();
      form.setValue("origin_city", location.city);
      form.setValue("origin_state", location.state);
    } catch (error) {
      toast.error(error.message || "Unable to fetch location.");
    }
  };

  const handleSaveLoad = async (loadArg = state.selectedLoad.data) => {
    const load = loadArg ? normalizeLoad(loadArg) : null;
    if (!load) {
      return;
    }
    if (!user?.email) {
      toast.error("Please log in to save loads.");
      return;
    }

    const loadId = getLoadIdentifier(load);
    const entryId = load.entry_id || `${user.email}-${loadId}`;
    const payload = buildSavePayload({ ...load, entry_id: entryId });

    dispatch({ type: "set-mode", mode: "saving", load });
    try {
      await saveLoadMutation.mutateAsync(payload);
      updateActiveResults(loadId, (result) => ({
        ...result,
        isSaved: true,
        entry_id: entryId,
      }));
      trackLoadSave({
        entry_id: entryId,
        rate: load.rate_per_mile_est || 0,
        miles: load.distance || 0,
        revenue: load.payment || 0,
        origin: `${load.origin_city || ""}, ${load.origin_state || ""}`,
        destination: `${load.destination_city || ""}, ${load.destination_state || ""
          }`,
      });
      toast.success("Load saved.");
      if (state.showSaved) {
        savedLoadsQuery.refetch();
      }
    } catch (error) {
      toast.error(error?.message || "Failed to save load.");
    } finally {
      dispatch({ type: "set-mode", mode: "idle" });
    }
  };

  const handleRemoveSavedLoad = async (loadArg = state.selectedLoad.data) => {
    const load = loadArg ? normalizeLoad(loadArg) : null;
    if (!load) {
      return;
    }
    if (!user?.email) {
      toast.error("Please log in to manage saved loads.");
      return;
    }
    const loadId = getLoadIdentifier(load);
    const entryId = load.entry_id || `${user.email}-${loadId}`;

    dispatch({ type: "set-mode", mode: "deleting", load });
    try {
      await deleteSavedLoadMutation.mutateAsync({ entryId });
      updateActiveResults(loadId, (result) => ({
        ...result,
        isSaved: false,
        entry_id: undefined,
      }));
      toast.success("Load removed.");
      savedLoadsQuery.refetch();
    } catch (error) {
      toast.error(error?.message || "Failed to remove load.");
    } finally {
      dispatch({ type: "set-mode", mode: "idle" });
    }
  };

  const handleSendCarrierPackageEmail = async (
    loadArg = state.selectedLoad.data,
  ) => {
    const load = loadArg ? normalizeLoad(loadArg) : null;
    if (!load) {
      return;
    }
    if (!load.company_email) {
      toast.error("No carrier email is available for this load.");
      return;
    }
    if (!user?.email) {
      toast.error("Please log in to send carrier packages.");
      return;
    }

    dispatch({ type: "set-mode", mode: "emailing", load });
    try {
      await sendCarrierPacket({ load, user });
      toast.success(`Carrier packet sent to ${load.company_email}.`);
    } catch (error) {
      toast.error(error?.message || "Failed to send carrier packet.");
    } finally {
      dispatch({ type: "set-mode", mode: "idle" });
    }
  };

  const handleBookLoad = async (loadArg = state.selectedLoad.data) => {
    const load = loadArg ? normalizeLoad(loadArg) : null;
    if (!load) {
      return;
    }
    if (!load.id) {
      toast.error("Unable to book this load because it is missing an ID.");
      return;
    }
    if (!profileData?.ch_t_number) {
      toast.error("Add your CH Robinson T-Number in your profile to book loads.");
      return;
    }

    dispatch({ type: "set-mode", mode: "booking", load });
    try {
      const payload = {
        loads: [
          {
            id: load.id,
            cost_type: load.cost_type || "Flat",
            code: load.code || "400",
            cost_description: load.cost_description || "Line Haul",
            sourceCostPerUnit:
              load.payment || load.rate || load.sourceCostPerUnit || 0,
            units: load.units || 1,
          },
        ],
      };
      const params = {
        carrier_code: profileData.ch_t_number,
        carrier_email: user.email,
        carrier_name: user.name || "Carrier",
      };
      const response = await bookLoadMutation.mutateAsync({ payload, params });
      if (response?.success) {
        trackLoadBook({
          id: load.id,
          rate: load.rate_per_mile_est || 0,
          miles: load.distance || 0,
          revenue: load.payment || 0,
          origin: `${load.origin_city || ""}, ${load.origin_state || ""}`,
          destination: `${load.destination_city || ""}, ${load.destination_state || ""
            }`,
        });
        toast.success("Load booked successfully.");
      } else {
        throw new Error("Failed to book load. Please try again.");
      }
    } catch (error) {
      toast.error(error?.message || "Failed to book load.");
    } finally {
      dispatch({ type: "set-mode", mode: "idle" });
    }
  };

  const handleDiscussLoad = (loadArg = state.selectedLoad.data) => {
    const load = loadArg ? normalizeLoad(loadArg) : null;
    if (!load) {
      return;
    }
    try {
      sessionStorage.setItem("selectedLoad", JSON.stringify(load));
    } catch (error) {
      // Ignore storage errors
    }
    navigate("/ai-voice-agent");
  };

  const isLoadSaved = useCallback(
    (load) => {
      if (!load) {
        return false;
      }
      if (load.isSaved) {
        return true;
      }
      const identifier = getLoadIdentifier(load);
      if (!identifier) {
        return false;
      }
      return savedLoads.some(
        (savedLoad) =>
          getLoadIdentifier(savedLoad) === identifier && savedLoad.isSaved,
      );
    },
    [savedLoads],
  );

  if (!activeTab) {
    return (
      <div className="h-full flex items-center justify-center bg-white text-sm text-tw-neutral-500">
        Loading search tab...
      </div>
    );
  }
  const isSSEPending = activeTab?.lastSearch?.ssePending;
  const isLoadingState = (activeTab?.loading || false) || isSSEPending;
  return (
    <>
      <NewLoadSearchView
        form={form}
        tabs={tabs}
        activeTab={activeTab}
        activeTabId={activeTabId}
        onTabChange={setActiveTabId}
        onAddTab={() => addTab("standard")}
        onRemoveTab={removeTab}
        showSearchForm={state.showSearchForm}
        onToggleSearchForm={() => dispatch({ type: "toggle-search-form" })}
        carriers={carriers}
        selectedCarrier={selectedCarrier}
        onCarrierChange={handleCarrierChange}
        isDispatcher={isDispatcher}
        enableBackhaul={isBackhaulEnabled}
        isTrialUser={isTrialUser}
        trialCount={trialCount}
        trialLimit={DAILY_SEARCH_LIMIT}
        searchLimitReached={searchLimitReached}
        onUpgrade={() => navigate("/subscribe")}
        isSearchLoading={isLoadingState || profileLoading}
        isSSEPending={isSSEPending} 
        onSubmit={onSubmit}
        error={state.error}
        showSaved={state.showSaved}
        onToggleSaved={handleToggleSavedLoads}
        onRefreshSaved={handleRefreshSavedLoads}
        isFetchingSaved={isFetchingSavedLoads}
        isInternalUser={isInternalUser}
        onDownloadCsv={handleDownloadCsv}
        sort={currentSort}
        onSortChange={handleSortChange}
        results={sortedResults}
        highlightMap={{}}
        backhaulResults={backhaulResults}
        resultsCount={resultsCount}
        showBackhaulView={showBackhaulView}
        onSelectLoad={handleSelectLoad}
        handleGetCurrentLocation={handleGetCurrentLocation}
        searchParams={searchParams}
        showPartners={showPartners}
        onTogglePartners={() => setShowPartners(!showPartners)}
        viewedLoadIds={viewedLoadIds}
        isSelectMode={isSelectMode}
        onToggleSelectMode={handleToggleSelectMode}
        selectedLoadIds={selectedLoadIds}
        onToggleLoadSelection={handleToggleLoadSelection}
        onMarkSelectedAsViewed={handleMarkSelectedAsViewed}
      />

      {state.sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/30 z-40"
          onClick={handleCloseSidebar}
          aria-hidden="true"
        />
      )}

      <LoadDetailsModal
        selectedLoad={state.selectedLoad.data}
        showSidebar={state.sidebarOpen}
        setShowSidebar={(value) =>
          value ? null : dispatch({ type: "close-sidebar" })
        }
        setShowGlobalOverlay={() => { }}
        searchParams={searchParams}
        theme="light"
        savingLoadId={
          state.selectedLoad.mode === "saving"
            ? getLoadIdentifier(state.selectedLoad.data)
            : null
        }
        deletingLoadId={
          state.selectedLoad.mode === "deleting"
            ? getLoadIdentifier(state.selectedLoad.data)
            : null
        }
        isLoadSaved={isLoadSaved}
        handleEmailDocuments={(_, load) => handleSendCarrierPackageEmail(load)}
        handleSaveLoad={handleSaveLoad}
        handleDeleteLoad={handleRemoveSavedLoad}
        handleBookLoad={handleBookLoad}
        handleDiscussLoad={handleDiscussLoad}
      />
    </>
  );
};

export default NewLoadSearch;

